import {
  CorporateHolderModel,
  IndividualHolderModel,
  SecurityRegistryRecord
} from "@models/shares/security-registry-record";
import { Address } from "@models/address/address";

export type ShareholderIcon = 'ic-accounts-16' | 'ic-my-account-16' | 'ic-companies-16' | '';

export interface SecurityholderName {
  name: string;
  previousName: string;
  entityNumber: string;
  isIndividual: boolean;
  relationshipId: string;
}

export class SecurityholderViewModel {
  record: SecurityRegistryRecord;
  previousRecord: SecurityRegistryRecord | null;

  iconClass: ShareholderIcon;
  holdersNames: SecurityholderName[];
  beneficialOwner: Record<string, (IndividualHolderModel | CorporateHolderModel)[]>;
  isBeneficialOwner: boolean;
  holding: string;
  number: number | null;
  previousNumber: number | null;
  fullyPaid: boolean;
  addresses: Address[];
  authorisedSignatory: string[][];

  constructor(data: Partial<SecurityholderViewModel> = {}) {
    this.record = data.record!;
    this.previousRecord = data.previousRecord ?? null;

    this.iconClass = this.getIconClass();
    this.holdersNames = this.getHoldersNames();
    this.beneficialOwner = this.record.beneficialOwnerGroups;
    this.isBeneficialOwner = this.record.isBeneficialOwner;
    this.holding = this.record.holdings?.[0]?.securityType?.class || this.record.validationError?.class || '-';
    this.number = this.record.holdings.length ? this.record.number : null;
    this.previousNumber = this.record.holdings.length ? this.record.previousNumber : null;
    this.fullyPaid = this.record.fullyPaid;
    this.addresses = this.record.holders.map((holder) => holder.address);
    this.authorisedSignatory = this.record.authorisedSignatory;
  }

  getIconClass(): ShareholderIcon {
    if (!this.record.holders.length) return '';
    if (this.record.holders.length > 1) return 'ic-accounts-16';
    if ('entityData' in this.record.holders[0]) return 'ic-companies-16';
    return 'ic-my-account-16';
  }

  getHoldersNames(holders = this.record.holders, previousRecord = this.previousRecord): SecurityholderName[] {
    const previousNames: Record<string, string> = {};

    if (previousRecord) {
      previousRecord.holders.forEach((holder) => {
        previousNames[holder.relationshipId] = holder.name;
      });
    }

    return holders.map((holder) => ({
      name: holder.name,
      previousName: `${ previousNames[holder.relationshipId] ?? '' }`,
      entityNumber: 'entityData' in holder ? holder.entityData.entityNumber ?? '' : '',
      isIndividual: 'individualData' in holder,
      relationshipId: holder.relationshipId,
    }));
  }

  fillPreviousRecord(previousRecord: SecurityRegistryRecord): void {
    this.previousRecord = previousRecord;
    this.previousNumber = previousRecord.number;
    this.holdersNames = this.getHoldersNames(this.record.holders, previousRecord);

    // Fill previous number value
    this.record.holdings.forEach((holding) => {
      const relatedPreviousHolding = previousRecord.holdings.find((prevHolding) => prevHolding.securityHoldingId == holding.securityHoldingId);

      if (relatedPreviousHolding) {
        holding.previousNumber = relatedPreviousHolding.number;
      }
    });
  }

  isPastShareholder(): boolean {
    const rule = this.record.number == 0 && this.record.nonAsic;

    if (this.previousRecord) {
      return rule && this.previousRecord.number == 0 && this.previousRecord.nonAsic;
    }

    return rule;
  }
}
